import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Check, ChevronRight, FileText, MessageCircle, Phone, RotateCw, Search, Sun } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/states";
import { vociDi } from "@/lib/accessi";
import { arrivo, erroreDi, giornoDi, giornoLungo, oggi, ora, passato, piuGiorni, statoCommerciale, testaGiorno } from "@/lib/crm";
import { eur } from "@/lib/formato";
import { cn } from "@/lib/utils";

/* «Oggi»: la prima pagina di chi vende (decisione del 30-10). Mette insieme tre letture che esistono già
   e una nuova: le visite di oggi e della settimana (/calendario), le conversazioni WhatsApp passate a lui
   che aspettano (/messaggi, quelle con l'escalation aperta, come il contatore del menu), i suoi preventivi
   fermi da più di una settimana (/crm/preventivi-fermi) e i suoi clienti in corsa (/crm/clienti).
   Ogni pezzo si chiede e si mostra solo se la sezione che lo legge è accesa per quella persona: con
   Calendario spento, niente visite. Il resto lo fa la RLS: ognuno vede ciò che vede già altrove.
   «Ho chiamato» non c'è: vuole un dato nuovo (una migrazione), e arriva dopo. */

const IN_CORSA = ["da_confermare", "appuntamento_fatto", "preventivo_inviato", "in_trattativa"];

function saluto() {
  const h = Number(
    new Intl.DateTimeFormat("it-IT", {
      hour: "numeric",
      hourCycle: "h23",
      timeZone: "Europe/Rome",
    }).format(new Date()),
  );
  return h < 13 ? "Buongiorno" : h < 18 ? "Buon pomeriggio" : "Buonasera";
}

/** «tra 4 ore» · «tra 20 minuti» · «adesso» · «passata»: quanto manca, detto come lo si dice. */
function traQuanto(inizio, fine) {
  const t = new Date(inizio).getTime();
  const adesso = Date.now();
  if (t <= adesso) return fine && new Date(fine).getTime() > adesso ? "adesso" : "passata";
  const min = Math.round((t - adesso) / 60000);
  if (min < 60) return `tra ${min} ${min === 1 ? "minuto" : "minuti"}`;
  const ore = Math.round(min / 60);
  return `tra ${ore} ${ore === 1 ? "ora" : "ore"}`;
}

/** Una lettura con il suo stato: si chiede solo se `attiva`. */
function useLettura(attiva, leggi) {
  const [s, setS] = useState({ fase: attiva ? "carico" : "spenta" });
  const carica = useCallback(() => {
    if (!attiva) return setS({ fase: "spenta" });
    setS((x) => (x.fase === "ok" ? x : { fase: "carico" }));
    leggi()
      .then((dati) => setS({ fase: "ok", dati }))
      .catch((e) => setS({ fase: "errore", testo: erroreDi(e) }));
  }, [attiva]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(carica, [carica]);
  return [s, carica];
}

function Blocco({ titolo, children, testId }) {
  return (
    <section className="grid grid-cols-[minmax(0,1fr)] gap-inside" data-testid={testId}>
      <h2 className="text-body-strong text-ink-2">{titolo}</h2>
      {children}
    </section>
  );
}

function Stato({ s, carica, righe = 2, children }) {
  if (s.fase === "carico")
    return (
      <div className="grid gap-inside" aria-busy="true">
        {Array.from({ length: righe }, (_, i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    );
  if (s.fase === "errore")
    return (
      <div
        role="alert"
        className="flex flex-wrap items-center justify-between gap-inside rounded-lg bg-danger-bg px-4 py-3 text-danger-ink"
      >
        <span>Non si è caricato: {s.testo}</span>
        <Button variant="outline" size="sm" className="h-11 md:h-8" onClick={carica}>
          <RotateCw />
          Riprova
        </Button>
      </div>
    );
  return children(s.dati);
}

function Icona({ icon: I, tono }) {
  return (
    <span
      className={cn(
        "inline-flex size-10 shrink-0 items-center justify-center rounded-md",
        tono === "ok"
          ? "bg-success-bg text-success-ink"
          : tono === "warn"
            ? "bg-warning-bg text-warning-ink"
            : "bg-brand-subtle text-brand-ink",
      )}
      aria-hidden
    >
      <I className="size-5" strokeWidth={1.75} />
    </span>
  );
}

function Visita({ a, messaggi, onEsito, busy }) {
  const daSegnare = a.tipo === "visita_showroom" && ["programmato", "riprogrammato"].includes(a.stato) && passato(a.data_inizio);
  const fatta = a.stato === "presentato";
  const letta = (messaggi || []).filter((m) => m.consegna === "read").pop();
  return (
    <div
      className={cn(
        "grid gap-group rounded-lg p-4 md:grid-cols-[auto_minmax(0,1fr)_auto] md:items-center",
        daSegnare ? "bg-warning-bg" : fatta ? "bg-surface-2" : "bg-brand-subtle",
      )}
      data-testid={`oggi-visita-${a.id}`}
    >
      <div className="flex items-baseline gap-2 md:grid md:min-w-16 md:gap-0">
        <span className="font-figure text-h2 text-ink-strong">{ora(a.data_inizio)}</span>
        <span className="text-caption font-normal text-ink-2">
          {fatta ? "fatta" : a.stato === "no_show" ? "non venuto" : traQuanto(a.data_inizio, a.data_fine)}
        </span>
      </div>
      <div className="min-w-0">
        <p className="truncate font-bold text-ink-strong">{a.cliente || "Senza cliente"}</p>
        <p className="truncate text-small text-ink-2">{[a.tipo_label, a.sede, a.note].filter(Boolean).join(" · ")}</p>
        {letta ? (
          <p className="mt-micro inline-flex items-center gap-1 text-caption text-success-ink">
            <Check className="size-3.5" aria-hidden />
            {letta.nome} · letto
          </p>
        ) : null}
      </div>
      {daSegnare ? (
        <div className="grid grid-cols-2 gap-inside md:flex">
          <span className="col-span-2 text-caption text-warning-ink md:hidden">Com'è andata?</span>
          <Button disabled={busy} onClick={() => onEsito(a, "presentato")} data-testid={`oggi-presentata-${a.id}`}>
            Presentata
          </Button>
          <Button
            variant="outline"
            disabled={busy}
            className="text-danger-ink"
            onClick={() => onEsito(a, "no_show")}
            data-testid={`oggi-noshow-${a.id}`}
          >
            No show
          </Button>
        </div>
      ) : a.cliente_id ? (
        <Button asChild variant="outline">
          <Link to={`/clienti/${a.cliente_id}`}>Apri la scheda</Link>
        </Button>
      ) : null}
    </div>
  );
}

export default function OggiPage() {
  const nav = useNavigate();
  const { profile } = useAuth() || {};
  const voci = vociDi(profile?.ruolo, profile?.sezioni_spente);
  const conCal = voci.includes("calendario");
  const conMsg = voci.includes("messaggi");
  const conPrev = voci.includes("preventivatore");
  const conClienti = voci.includes("clienti");
  const g = oggi();
  const [cerca, setCerca] = useState("");
  const [busy, setBusy] = useState(false);
  const [messaggiProssima, setMessaggiProssima] = useState(null);
  const [tuttiIFermi, setTuttiIFermi] = useState(false);

  const [cal, rileggiCal] = useLettura(conCal, () => api.get("/calendario", { params: { da: g, a: piuGiorni(g, 6) } }).then((r) => r.data));
  const [msg, rileggiMsg] = useLettura(conMsg, () =>
    api.get("/messaggi").then((r) => (r.data?.conversazioni || []).filter((c) => c.escalation > 0)),
  );
  const [fermi, rileggiFermi] = useLettura(conPrev, () => api.get("/crm/preventivi-fermi").then((r) => r.data?.righe || []));
  const [clienti, rileggiClienti] = useLettura(conClienti, () => api.get("/crm/clienti").then((r) => r.data?.righe || []));

  /* Le sue: chi vede anche i colleghi (il responsabile di sede, 0043) qui trova solo la propria giornata. */
  const appuntamenti = (cal.dati?.appuntamenti || []).filter(
    (a) => a.stato !== "annullato" && (!cal.dati?.io || a.venditore_id === cal.dati.io),
  );
  const diOggi = appuntamenti.filter((a) => giornoDi(a.data_inizio) === g);
  const prossima = diOggi.find(
    (a) => a.tipo === "visita_showroom" && ["programmato", "riprogrammato"].includes(a.stato) && !passato(a.data_inizio),
  );

  /* Lo stato dei WhatsApp automatici (letto) della prossima visita: una lettura sola, la sua. */
  useEffect(() => {
    if (!prossima) return undefined;
    let vivo = true;
    api
      .get(`/calendario/${prossima.id}`)
      .then(
        (r) =>
          vivo &&
          setMessaggiProssima({
            id: prossima.id,
            lista: r.data?.messaggi_automatici || [],
          }),
      )
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, [prossima?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const esito = async (a, stato) => {
    setBusy(true);
    try {
      await api.patch(`/calendario/${a.id}`, { stato });
      toast.success(
        stato === "presentato"
          ? `${a.cliente}: presentata`
          : `${a.cliente}: non è venuto${a.da_cal ? ". Il messaggio di recupero va in coda." : ""}`,
      );
      rileggiCal();
      rileggiClienti();
    } catch (e) {
      toast.error(erroreDi(e));
    } finally {
      setBusy(false);
    }
  };

  const daFare =
    diOggi.filter((a) => ["programmato", "riprogrammato"].includes(a.stato)).length + (msg.dati?.length || 0) + (fermi.dati?.length || 0);
  const nome = (profile?.nome || "").split(" ")[0];
  const nessunaSezione = !conCal && !conMsg && !conPrev && !conClienti;

  return (
    <section data-testid="page-oggi" className="grid grid-cols-[minmax(0,1fr)] gap-card">
      <header className="flex flex-col gap-group md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <h1 className="font-display text-h1-mobile text-ink-strong md:text-h1">
            {saluto()}
            {nome ? `, ${nome}` : ""}
          </h1>
          <p className="mt-micro text-body text-ink-2 first-letter:uppercase" data-testid="oggi-sottotitolo">
            {[giornoLungo(g), cal.fase === "carico" && conCal ? null : `${daFare} ${daFare === 1 ? "cosa da fare" : "cose da fare"}`]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
        {conClienti ? (
          <form
            role="search"
            className="relative w-full md:w-80"
            onSubmit={(e) => {
              e.preventDefault();
              if (cerca.trim()) nav(`/clienti?vista=lista&q=${encodeURIComponent(cerca.trim())}`);
            }}
          >
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3" aria-hidden />
            <Input
              type="search"
              value={cerca}
              onChange={(e) => setCerca(e.target.value)}
              placeholder="Cerca un cliente, anche per telefono"
              aria-label="Cerca un cliente, anche per telefono"
              className="pl-9"
              data-testid="oggi-cerca"
            />
          </form>
        ) : null}
      </header>

      {nessunaSezione ? (
        <div className="rounded-xl border border-line bg-surface">
          <EmptyState icon={Sun} tone="brand" title="La tua giornata compare qui" className="py-section">
            Visite, messaggi e preventivi da riprendere arrivano quando il titolare ti accende Calendario, Messaggi o Preventivi.
          </EmptyState>
        </div>
      ) : (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-card lg:grid-cols-[minmax(0,1fr)_minmax(0,22.5rem)]">
          <div className="grid grid-cols-[minmax(0,1fr)] content-start gap-card rounded-xl border border-line bg-surface p-4 md:p-card">
            <div className="flex items-baseline justify-between gap-group">
              <h2 className="font-display text-card-title text-ink-strong">Da fare oggi</h2>
            </div>

            {conCal ? (
              <Blocco titolo="In agenda oggi" testId="oggi-visite">
                <Stato s={cal} carica={rileggiCal}>
                  {() =>
                    diOggi.length ? (
                      <div className="flex flex-col gap-inside">
                        {diOggi.map((a) => (
                          <Visita
                            key={a.id}
                            a={a}
                            busy={busy}
                            onEsito={esito}
                            messaggi={messaggiProssima?.id === a.id ? messaggiProssima.lista : null}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center justify-between gap-inside">
                        <p className="text-ink-2">Nessun appuntamento oggi.</p>
                        <Button asChild variant="outline">
                          <Link to="/calendario?nuovo=1">Fissa un appuntamento</Link>
                        </Button>
                      </div>
                    )
                  }
                </Stato>
              </Blocco>
            ) : null}

            {conMsg ? (
              <Blocco titolo="Passate a te su WhatsApp" testId="oggi-messaggi">
                <Stato s={msg} carica={rileggiMsg}>
                  {(lista) =>
                    lista.length ? (
                      <ul className="flex flex-col gap-inside">
                        {lista.map((c) => (
                          <li
                            key={c.wa_id}
                            className="flex flex-col gap-inside rounded-lg border border-line-subtle p-3 md:flex-row md:items-center"
                          >
                            <div className="flex min-w-0 flex-1 gap-3">
                              <Icona icon={MessageCircle} tono="ok" />
                              <div className="min-w-0">
                                <p className="truncate">
                                  <span className="font-bold text-ink-strong">{c.nome}</span>
                                  <span className="text-ink-2"> · {arrivo(c.attivita_at)}</span>
                                </p>
                                {c.ultimo?.testo ? <p className="line-clamp-2 text-small text-ink-2">«{c.ultimo.testo}»</p> : null}
                              </div>
                            </div>
                            <Button asChild>
                              <Link to={`/messaggi?contatto=${c.wa_id}`} data-testid={`oggi-conversazione-${c.wa_id}`}>
                                Apri la conversazione
                              </Link>
                            </Button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-ink-2">Nessuna conversazione ti aspetta.</p>
                    )
                  }
                </Stato>
              </Blocco>
            ) : null}

            {conPrev ? (
              <Blocco titolo="Preventivi senza risposta da più di una settimana" testId="oggi-fermi">
                <Stato s={fermi} carica={rileggiFermi}>
                  {(lista) =>
                    lista.length ? (
                      <>
                        <ul className="flex flex-col gap-inside">
                          {(tuttiIFermi ? lista : lista.slice(0, 5)).map((p) => (
                            <li
                              key={p.id}
                              className="flex flex-col gap-inside rounded-lg border border-line-subtle p-3 md:flex-row md:items-center"
                              data-testid={`oggi-fermo-${p.id}`}
                            >
                              <div className="flex min-w-0 flex-1 gap-3">
                                <Icona icon={FileText} tono="warn" />
                                <div className="min-w-0">
                                  <p className="truncate font-bold text-ink-strong">{p.cliente}</p>
                                  <p className="truncate text-small text-ink-2">
                                    {[p.titolo, `inviato ${giornoLungo(giornoDi(p.inviato_at))}`, `${p.giorni} giorni fa`]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </p>
                                </div>
                                {p.valore ? (
                                  <span className="shrink-0 font-bold tabular-nums text-ink-strong md:hidden">
                                    {eur(p.valore, { decimali: 0 })}
                                  </span>
                                ) : null}
                              </div>
                              <div className="flex items-center gap-inside">
                                {p.valore ? (
                                  <span className="hidden font-bold tabular-nums text-ink-strong md:inline">
                                    {eur(p.valore, { decimali: 0 })}
                                  </span>
                                ) : null}
                                {p.telefono ? (
                                  <Button asChild variant="outline" size="icon" className="size-11 md:size-9">
                                    <a href={`tel:${p.telefono_e164 || p.telefono}`} aria-label={`Chiama ${p.cliente}`}>
                                      <Phone />
                                    </a>
                                  </Button>
                                ) : null}
                                <Button asChild variant="outline" className="flex-1 md:flex-none">
                                  <Link to={`/preventivi/preventivatore/${p.id}`}>Apri il preventivo</Link>
                                </Button>
                              </div>
                            </li>
                          ))}
                        </ul>
                        {lista.length > 5 && !tuttiIFermi ? (
                          <Button
                            variant="ghost"
                            className="justify-self-start text-brand-ink"
                            onClick={() => setTuttiIFermi(true)}
                            data-testid="oggi-fermi-altri"
                          >
                            Mostra gli altri {lista.length - 5}
                          </Button>
                        ) : null}
                      </>
                    ) : (
                      <p className="text-ink-2">Nessun preventivo fermo da più di una settimana.</p>
                    )
                  }
                </Stato>
              </Blocco>
            ) : null}

            {!conCal && !conMsg && !conPrev ? (
              <p className="text-ink-2">Le sezioni che riempiono questa lista sono spente per te.</p>
            ) : null}
          </div>

          <div className="grid grid-cols-[minmax(0,1fr)] content-start gap-card">
            {conCal ? (
              <section className="rounded-xl border border-line bg-surface p-4 md:p-card" data-testid="oggi-settimana">
                <h2 className="font-display text-card-title text-ink-strong">La mia settimana</h2>
                <div className="mt-group">
                  <Stato s={cal} carica={rileggiCal} righe={3}>
                    {() =>
                      appuntamenti.length ? (
                        <ol className="grid">
                          {appuntamenti.slice(0, 6).map((a) => {
                            const giorno = giornoDi(a.data_inizio);
                            return (
                              <li key={a.id} className="border-b border-line-subtle last:border-b-0">
                                <Link
                                  to={`/calendario?appuntamento=${a.id}`}
                                  className="grid min-h-12 grid-cols-[4.5rem_minmax(0,1fr)] gap-group py-2 hover:bg-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand"
                                >
                                  <span
                                    className={cn(
                                      "tabular-nums first-letter:uppercase",
                                      giorno === g ? "font-bold text-brand-ink" : "font-semibold text-ink",
                                    )}
                                  >
                                    {giorno === g ? "Oggi" : testaGiorno(giorno)}
                                    <span className="block font-normal text-ink-2">{ora(a.data_inizio)}</span>
                                  </span>
                                  <span className="min-w-0">
                                    <span className="block truncate font-semibold text-ink-strong">{a.cliente || "Senza cliente"}</span>
                                    <span className="block truncate text-small text-ink-2">
                                      {[a.tipo_label, a.sede].filter(Boolean).join(" · ")}
                                    </span>
                                  </span>
                                </Link>
                              </li>
                            );
                          })}
                        </ol>
                      ) : (
                        <p className="text-ink-2">Niente in agenda nei prossimi sette giorni.</p>
                      )
                    }
                  </Stato>
                  <Link
                    to="/calendario"
                    className="mt-group inline-flex min-h-11 items-center gap-1.5 font-semibold text-brand-ink hover:underline md:min-h-8"
                  >
                    <CalendarDays className="size-4" aria-hidden />
                    Apri il calendario
                  </Link>
                </div>
              </section>
            ) : null}

            {conClienti ? (
              <section className="rounded-xl border border-line bg-surface p-4 md:p-card" data-testid="oggi-clienti">
                <h2 className="font-display text-card-title text-ink-strong">I miei clienti in corsa</h2>
                <p className="mt-micro text-small text-ink-2">Quelli che segui tu, fase per fase.</p>
                <div className="mt-group">
                  <Stato s={clienti} carica={rileggiClienti} righe={3}>
                    {(righe) => (
                      <ul>
                        {IN_CORSA.map((codice) => {
                          const s = statoCommerciale(codice);
                          const n = righe.filter((r) => r.stato === codice && r.venditore_id === profile?.id).length;
                          return (
                            <li key={codice} className="border-b border-line-subtle last:border-b-0">
                              <Link
                                to={`/clienti?vista=kanban`}
                                className="flex min-h-11 items-center gap-3 hover:bg-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand"
                              >
                                <span className={cn("size-2 shrink-0 rounded-full", s.punto)} aria-hidden />
                                <span className="flex-1 text-ink">{s.label}</span>
                                <span className="font-bold tabular-nums text-ink-strong">{n}</span>
                                <ChevronRight className="size-4 text-ink-3" aria-hidden />
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </Stato>
                </div>
              </section>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
}
